import './coapplicantEditIncomePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, uniqueId } from 'lodash';
import { useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { additionalIncomeTypeOptions } from '../../../store/applicantStore/constants/additionalIncomeTypeOptions';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const {
    computed: { coapplicantReviewEditPathname, coapplicantReviewEditProgress },
    coapplicantAdditionalIncomeAmount,
    coapplicantAdditionalIncomeDurationMonths,
    coapplicantAdditionalIncomeDurationYears,
    coapplicantAdditionalIncomeEmployerName,
    coapplicantAdditionalIncomeFrequency,
    coapplicantAdditionalIncomeJobTitle,
    coapplicantAdditionalIncomeType,
    hasAdditionalIncome,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantAdditionalIncomeAmount,
    setCoapplicantAdditionalIncomeDurationMonths,
    setCoapplicantAdditionalIncomeDurationYears,
    setCoapplicantAdditionalIncomeEmployerName,
    setCoapplicantAdditionalIncomeFrequency,
    setCoapplicantAdditionalIncomeJobTitle,
    setCoapplicantAdditionalIncomeType,
    setHasAdditionalIncome,
    syncCoapplicant,
  } = useCoapplicantStore();

  const shouldShowIncomeFields =
    watch('hasAdditionalIncomeValue', hasAdditionalIncome) === YES;

  const additionalIncomeValidation = {
    required: t('validation.additionalIncome.required'),
  };

  const incomeTypeValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.incomeType.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeFrequencyValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.incomeFrequency.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeAmountValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      setValueAs: (value: string): string =>
        inputEventHandler.currency.parser(value),
      required: t('validation.incomeAmount.required'),
      min: {
        value: 0,
        message: t('validation.incomeAmount.min', { min: '$0' }),
      },
    };
  }, [shouldShowIncomeFields]);

  const incomeEmployerNameValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.employerName.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeJobTitleValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.jobTitle.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeDurationYearsValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.durationYears.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeDurationMonthsValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.durationMonths.required'),
    };
  }, [shouldShowIncomeFields]);

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue', coapplicantAdditionalIncomeType) ===
    IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      hasAdditionalIncomeValue,
      incomeAmountValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeTypeValue,
    } = values;

    setHasAdditionalIncome(hasAdditionalIncomeValue);

    if (shouldShowIncomeFields) {
      setCoapplicantAdditionalIncomeAmount(incomeAmountValue);
      setCoapplicantAdditionalIncomeFrequency(incomeFrequencyValue);
      setCoapplicantAdditionalIncomeType(incomeTypeValue);

      if (showSecondaryEmploymentFields) {
        setCoapplicantAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
        setCoapplicantAdditionalIncomeDurationYears(incomeDurationYearsValue);
        setCoapplicantAdditionalIncomeEmployerName(incomeEmployerNameValue);
        setCoapplicantAdditionalIncomeJobTitle(incomeJobTitleValue);
      } else {
        setCoapplicantAdditionalIncomeDurationMonths(null);
        setCoapplicantAdditionalIncomeDurationYears(null);
        setCoapplicantAdditionalIncomeEmployerName(null);
        setCoapplicantAdditionalIncomeJobTitle(null);
      }
    } else {
      setCoapplicantAdditionalIncomeAmount(null);
      setCoapplicantAdditionalIncomeFrequency(null);
      setCoapplicantAdditionalIncomeType(null);
      setCoapplicantAdditionalIncomeDurationMonths(null);
      setCoapplicantAdditionalIncomeDurationYears(null);
      setCoapplicantAdditionalIncomeEmployerName(null);
      setCoapplicantAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-income-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.coapplicantEditIncome.header.root}>
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantEditIncome.header.previousStepButton
            }
            type="button"
            onClick={finishEditing}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantEditIncome.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantEditIncome.progress.root}
            value={coapplicantReviewEditProgress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantEditIncome.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditIncome.form.h1}>
                {t('CoapplicantEditIncomePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditIncome.form.h2}>
                {t('CoapplicantEditIncomePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditIncome.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register(
                      'hasAdditionalIncomeValue',
                      additionalIncomeValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.hasAdditionalIncomeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditIncome.form
                          .hasAdditionalIncomeSelect,
                    }}
                    label={t('CoapplicantEditIncomePage.additionalIncome')}
                    value={watch(
                      'hasAdditionalIncomeValue',
                      hasAdditionalIncome || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantEditIncomePage.additionalIncome')}
                    </MenuItem>

                    {additionalIncomeTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={uniqueId(value)} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.hasAdditionalIncomeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditIncome.form
                          .hasAdditionalIncomeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.hasAdditionalIncomeValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeTypeValue', incomeTypeValidation)}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    disabled={!shouldShowIncomeFields}
                    error={
                      shouldShowIncomeFields && !isEmpty(errors.incomeTypeValue)
                    }
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditIncome.form
                          .incomeTypeSelect,
                    }}
                    label={t('CoapplicantEditIncomePage.incomeType')}
                    value={
                      shouldShowIncomeFields
                        ? watch(
                            'incomeTypeValue',
                            coapplicantAdditionalIncomeType || '',
                          ) || ''
                        : ''
                    }
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantEditIncomePage.incomeType')}
                    </MenuItem>

                    {incomeTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={uniqueId(value)} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.incomeTypeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditIncome.form
                          .incomeTypeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeTypeValue.message)}
                    </span>
                  )}
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      disabled={!shouldShowIncomeFields}
                      error={!isEmpty(errors.employerNameValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantEditIncome.form
                            .employerNameInput,
                      }}
                      label={t('CoapplicantEditIncomePage.employerName')}
                      value={
                        shouldShowIncomeFields
                          ? watch(
                              'incomeEmployerNameValue',
                              coapplicantAdditionalIncomeEmployerName || '',
                            ) || ''
                          : ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeEmployerNameValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantEditIncome.form
                            .employerNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      disabled={!shouldShowIncomeFields}
                      error={!isEmpty(errors.jobTitleValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantEditIncome.form
                            .jobTitleInput,
                      }}
                      label={t('CoapplicantEditIncomePage.jobTitle')}
                      value={
                        shouldShowIncomeFields
                          ? watch(
                              'incomeJobTitleValue',
                              coapplicantAdditionalIncomeJobTitle || '',
                            ) || ''
                          : ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeJobTitleValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantEditIncome.form
                            .jobTitleInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <TextField
                        {...register(
                          'incomeDurationYearsValue',
                          incomeDurationYearsValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        disabled={!shouldShowIncomeFields}
                        error={!isEmpty(errors.durationYearsValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditIncome.form.yearSelect,
                        }}
                        label={t('CoapplicantEditIncomePage.durationYears')}
                        value={
                          shouldShowIncomeFields
                            ? watch(
                                'incomeDurationYearsValue',
                                coapplicantAdditionalIncomeDurationYears,
                              ) ?? ''
                            : ''
                        }
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('CoapplicantEditIncomePage.durationYears')}
                        </MenuItem>

                        {Array.from({ length: 11 }, (_, index) => {
                          return (
                            <MenuItem
                              key={uniqueId(`duration-years-${index}`)}
                              value={index}
                            >
                              {index}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.incomeDurationYearsValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditIncome.form
                              .yearSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.incomeDurationYearsValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register(
                          'incomeDurationMonthsValue',
                          incomeDurationMonthsValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        disabled={!shouldShowIncomeFields}
                        error={!isEmpty(errors.durationMonthsValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditIncome.form
                              .monthSelect,
                        }}
                        label={t('CoapplicantEditIncomePage.durationMonths')}
                        value={
                          shouldShowIncomeFields
                            ? watch(
                                'incomeDurationMonthsValue',
                                coapplicantAdditionalIncomeDurationMonths,
                              ) ?? ''
                            : ''
                        }
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('CoapplicantEditIncomePage.durationMonths')}
                        </MenuItem>

                        {Array.from({ length: 12 }, (_, index) => {
                          return (
                            <MenuItem
                              key={uniqueId(`duration-months-${index}`)}
                              value={index}
                            >
                              {index}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.incomeDurationMonthsValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditIncome.form
                              .monthSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.incomeDurationMonthsValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'incomeFrequencyValue',
                      incomeFrequencyValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    disabled={!shouldShowIncomeFields}
                    error={
                      shouldShowIncomeFields &&
                      !isEmpty(errors.incomeFrequencyValue)
                    }
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditIncome.form
                          .frequencySelect,
                    }}
                    label={t('CoapplicantEditIncomePage.incomeFrequency')}
                    value={
                      shouldShowIncomeFields
                        ? watch(
                            'incomeFrequencyValue',
                            coapplicantAdditionalIncomeFrequency || '',
                          ) || ''
                        : ''
                    }
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantEditIncomePage.incomeFrequency')}
                    </MenuItem>

                    {incomeFrequencyOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.incomeFrequencyValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditIncome.form
                          .frequencySelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeFrequencyValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    disabled={!shouldShowIncomeFields}
                    error={
                      shouldShowIncomeFields &&
                      !isEmpty(errors.incomeAmountValue)
                    }
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditIncome.form.amountInput,
                      inputMode: 'numeric',
                    }}
                    label={t('CoapplicantEditIncomePage.incomeAmount')}
                    type="text"
                    value={
                      shouldShowIncomeFields
                        ? inputEventHandler.currency.formatter(
                            watch(
                              'incomeAmountValue',
                              coapplicantAdditionalIncomeAmount,
                            ),
                          )
                        : ''
                    }
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'incomeAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.incomeAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditIncome.form
                          .amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeAmountValue.message)}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <LoadingButton
                    color="secondary"
                    data-testid={
                      locator.pages.coapplicantEditIncome.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditIncomePage.cancel')}
                  </LoadingButton>

                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.coapplicantEditIncome.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isSyncingCoapplicant}
                    loadingPosition="start"
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('CoapplicantEditIncomePage.save')}
                  </LoadingButton>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditIncome.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditIncomePage';
